import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoadingSpinner from "../packages/ui/src/components/LoadingSpinner";
import Analytics from "./components/Analytics";

// Eagerly loaded components
import SurveyList from "./components/SurveyList";
import SurveyForm from "./components/SurveyForm";
import ThankYou from "./components/ThankYou";
import WebhookTest from "./components/WebhookTest";
import ContactButton from "./components/ContactButton";
import ContactModal from "./components/ContactModal";
import BookingForm from "./components/BookingForm";
import CalWidget from "./components/CalWidget";
import LabSection from "./components/LabSection";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminVideos from "./components/AdminVideos";
import AdminCRMContacts from "./components/AdminCRMContacts";
import AdminCRMContactForm from "./components/AdminCRMContactForm";
import AdminCRMNotes from "./components/AdminCRMNotes";
import AdminCTAs from "./components/AdminCTAs";
import AdminLabExercises from "./components/AdminLabExercises";
import AdminCourseRegistrations from "./components/AdminCourseRegistrations";
import AdminLabExerciseEditor from "./components/AdminLabExerciseEditor";
import AdminCTAEditor from "./components/AdminCTAEditor";
import AdminPostEditor from "./components/AdminPostEditor";
import AdminPromptDownloads from "./components/AdminPromptDownloads";
import BlogPostWithDownload from "./components/BlogPostWithDownload";
import LabExercisesList from "./components/LabExercisesList";
import UpdateBlogPostContent from "./components/UpdateBlogPostContent";
import AdminUploads from "./components/AdminUploads";
import AdminSearchAnalytics from "./components/AdminSearchAnalytics";
import GenerateSitemap from "./components/GenerateSitemap";
import SurveyDisplay from "./components/SurveyDisplay";
import CourseRegistration from "./components/CourseRegistration";
import ComponentLibraryDemo from "./components/ComponentLibraryDemo";
// Removed EmailTest component - no longer needed
// PromptLibrary moved to lazy loading to prevent parse-numeric-range issues
import PromptAdminRoutes from "./components/prompt-admin/PromptAdminRoutes";
import PromptAdminLayout from "./components/prompt-admin/PromptAdminLayout";
import PromptAdminLogin from "./components/prompt-admin/PromptAdminLogin";
import PromptAdminDashboard from "./components/prompt-admin/PromptAdminDashboard";
import PromptList from "./components/prompt-admin/PromptList";
import PromptAdminCategoryList from "./components/prompt-admin/CategoryList";
import TagList from "./components/prompt-admin/TagList";

// Lazily loaded components
const LandingPage = lazy(() => import("./components/LandingPage"));
const CourseCatalog = lazy(() => import("./components/CourseCatalog"));
const CourseDetail = lazy(() => import("./components/CourseDetail"));
const CourseOutline = lazy(() => import("./components/CourseOutline"));
const FractionalPMPage = lazy(() => import("./components/FractionalPMPage"));
const AIWorkshopPage = lazy(() => import("./components/AIWorkshopPage"));
const BlogLayout = lazy(() => import("./components/BlogLayout"));
const BlogList = lazy(() => import("./components/BlogList"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const CategoryList = lazy(() => import("./components/CategoryList"));
const AuthorList = lazy(() => import("./components/AuthorList"));
const CategoryIndex = lazy(() => import("./components/CategoryIndex"));
const AuthorIndex = lazy(() => import("./components/AuthorIndex"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminLogin = lazy(() => import("./components/AdminLogin"));
const AdminPosts = lazy(() => import("./components/AdminPosts"));
const AdminAuthors = lazy(() => import("./components/AdminAuthors"));
const AdminCategories = lazy(() => import("./components/AdminCategories"));
const AdminTags = lazy(() => import("./components/AdminTags"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const AdminCourses = lazy(() => import("./components/AdminCourses"));
const AdminCourseEditor = lazy(() => import("./components/AdminCourseEditor"));
const AdminCourseCategories = lazy(
  () => import("./components/AdminCourseCategories"),
);
const AdminCourseTags = lazy(() => import("./components/AdminCourseTags"));
const AdminAssets = lazy(() => import("./components/AdminAssets"));
const TimelinePage = lazy(() => import("./components/TimelinePage"));
const PromptForm = lazy(() => import("./components/prompt-admin/PromptForm"));
const PromptLibrary = lazy(() => import("./components/PromptLibrary"));
const PromptDetail = lazy(() => import("./components/PromptDetail"));
const SearchResultsPage = lazy(() => import("./components/SearchResultsPage"));

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [isBookingOpen, setIsBookingOpen] = React.useState(false);
  const [isCalOpen, setIsCalOpen] = React.useState(false);

  // Listen for custom event to open Cal widget
  useEffect(() => {
    const handleOpenCalWidget = () => {
      setIsCalOpen(true);
    };

    window.addEventListener("open-cal-widget", handleOpenCalWidget);

    return () => {
      window.removeEventListener("open-cal-widget", handleOpenCalWidget);
    };
  }, []);

  // Route-based intelligent preloading
  useEffect(() => {
    const preloadComponent = async (
      importFn: () => Promise<any>,
      retries = 3,
      delay = 1000,
    ) => {
      try {
        await importFn();
      } catch (error) {
        if (retries > 0) {
          console.log(`Retrying import... (${retries} attempts remaining)`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return preloadComponent(importFn, retries - 1, delay * 1.5);
        }
        console.error("Failed to preload component after all retries:", error);
      }
    };

    const preloadRouteComponents = async () => {
      // Only preload components for current route and adjacent routes
      const currentPath = window.location.pathname;

      if (currentPath === "/") {
        // Preload only landing page related components
        await preloadComponent(() => import("./components/LandingPage"));
        // Preload adjacent routes that users are likely to visit
        await preloadComponent(() => import("./components/AboutPage"));
        await preloadComponent(() => import("./components/CourseCatalog"));
      } else if (currentPath.startsWith("/blog")) {
        // Preload blog-related components
        await preloadComponent(() => import("./components/BlogLayout"));
        await preloadComponent(() => import("./components/BlogList"));
        await preloadComponent(() => import("./components/BlogPost"));
        await preloadComponent(() => import("./components/CategoryList"));
        await preloadComponent(() => import("./components/AuthorList"));
      } else if (currentPath.startsWith("/course")) {
        // Preload course-related components
        await preloadComponent(() => import("./components/CourseDetail"));
        await preloadComponent(() => import("./components/CourseOutline"));
        await preloadComponent(() => import("./components/CourseRegistration"));
      } else if (
        currentPath.startsWith("/admin") ||
        currentPath.startsWith("/prompt-admin")
      ) {
        // Preload admin-related components
        await preloadComponent(() => import("./components/AdminLayout"));
        await preloadComponent(() => import("./components/AdminLogin"));
        await preloadComponent(() => import("./components/AdminPosts"));
        await preloadComponent(() => import("./components/AdminCourses"));
      }
    };

    // Start preloading after a short delay to ensure dev server is ready
    const timer = setTimeout(() => {
      preloadRouteComponents().catch((error) => {
        console.error("Error during route-based preloading:", error);
      });
    }, 1000); // Reduced delay from 2000ms to 1000ms

    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <Analytics measurementId="G-6QQMV5RY9V" />

      <div className="min-h-screen bg-gray-50">
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={
                <LandingPage
                  onContactClick={() => setIsContactModalOpen(true)}
                  onCalClick={() => setIsCalOpen(true)}
                />
              }
            />
            <Route path="/courses" element={<CourseCatalog />} />
            <Route path="/course/:slug" element={<CourseDetail />} />
            <Route path="/course-outline" element={<CourseOutline />} />
            <Route path="/ai-workshop" element={<AIWorkshopPage />} />
            <Route
              path="/fractional-pm"
              element={
                <FractionalPMPage
                  onCalendarClick={() => setIsCalOpen(true)}
                  onContactClick={() => setIsContactModalOpen(true)}
                />
              }
            />
            <Route path="/survey" element={<SurveyList />} />
            <Route path="/survey/:id" element={<SurveyForm />} />
            <Route path="/survey/display/:id" element={<SurveyDisplay />} />
            <Route path="/prompt-library" element={<PromptLibrary />} />
            <Route path="/prompt/:slug" element={<PromptDetail />} />
            <Route path="/register" element={<CourseRegistration />} />
            <Route
              path="/thank-you"
              element={<ThankYou onCalClick={() => setIsCalOpen(true)} />}
            />
            <Route path="/webhook-test" element={<WebhookTest />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/prompt-admin/*" element={<PromptAdminRoutes />} />
            <Route
              path="/update-blog-post"
              element={<UpdateBlogPostContent />}
            />
            <Route path="/ui-components" element={<ComponentLibraryDemo />} />

            {/* Email Test */}
            {/* Removed EmailTest route - no longer needed */}

            {/* Lab Exercises */}
            <Route path="/lab-exercises" element={<LabExercisesList />} />

            <Route path="/blog" element={<BlogLayout />}>
              <Route index element={<BlogList />} />
              <Route path="categories" element={<CategoryIndex />} />
              <Route path="authors" element={<AuthorIndex />} />
              <Route path="category/:category" element={<CategoryList />} />
              <Route path="author/:author" element={<AuthorList />} />
              <Route path=":slug" element={<BlogPost />} />
            </Route>

            {/* Special route for the non-profit AI blog post */}
            <Route
              path="/blog/why-non-profits-cant-afford-to-ignore-ai-any-longer"
              element={
                <BlogLayout>
                  <BlogPostWithDownload slug="why-non-profits-cant-afford-ignore-ai-any-longer" />
                </BlogLayout>
              }
            />

            {/* Prompt Admin Routes */}
            <Route path="/prompt-admin/login" element={<PromptAdminLogin />} />
            <Route path="/prompt-admin" element={<PromptAdminLayout />}>
              <Route index element={<PromptAdminDashboard />} />
              <Route path="prompts" element={<PromptList />} />
              <Route path="prompts/new" element={<PromptForm />} />
              <Route path="prompts/edit/:id" element={<PromptForm />} />
              <Route path="categories" element={<PromptAdminCategoryList />} />
              <Route path="tags" element={<TagList />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={null} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="posts/new" element={<AdminPostEditor />} />
              <Route path="posts/:id" element={<AdminPostEditor />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="courses/new" element={<AdminCourseEditor />} />
              <Route path="courses/:id" element={<AdminCourseEditor />} />
              <Route
                path="course-categories"
                element={<AdminCourseCategories />}
              />
              <Route path="course-tags" element={<AdminCourseTags />} />
              <Route path="authors" element={<AdminAuthors />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="tags" element={<AdminTags />} />
              <Route path="generate-sitemap" element={<GenerateSitemap />} />
              <Route path="assets" element={<AdminAssets />} />
              <Route path="videos" element={<AdminVideos />} />
              <Route path="uploads" element={<AdminUploads />} />
              <Route path="ctas" element={<AdminCTAs />} />
              <Route path="ctas/new" element={<AdminCTAEditor />} />
              <Route path="lab-exercises" element={<AdminLabExercises />} />
              <Route path="ctas/:id" element={<AdminCTAEditor />} />
              <Route
                path="lab-exercises/new"
                element={<AdminLabExerciseEditor />}
              />
              <Route
                path="lab-exercises/:id"
                element={<AdminLabExerciseEditor />}
              />
              <Route
                path="course-registrations"
                element={<AdminCourseRegistrations />}
              />
              <Route
                path="prompt-downloads"
                element={<AdminPromptDownloads />}
              />
              <Route
                path="search-analytics"
                element={<AdminSearchAnalytics />}
              />
              {/* CRM Routes */}
              <Route path="crm/contacts" element={<AdminCRMContacts />} />
              <Route
                path="crm/contacts/new"
                element={<AdminCRMContactForm />}
              />
              <Route
                path="crm/contacts/:id"
                element={<AdminCRMContactForm />}
              />
              <Route
                path="crm/contacts/:id/notes"
                element={<AdminCRMNotes />}
              />
            </Route>

            {/* Search Results Page */}
            <Route
              path="/search"
              element={
                <SearchResultsPage
                  onContactClick={() => setIsContactModalOpen(true)}
                />
              }
            />
          </Routes>
        </Suspense>

        <ContactButton
          onClick={() => setIsContactModalOpen(true)}
          onCalendarClick={() => setIsBookingOpen(true)}
          onCalClick={() => setIsCalOpen(true)}
        />
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        />
        <BookingForm
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
        />
        <CalWidget isOpen={isCalOpen} onClose={() => setIsCalOpen(false)} />
      </div>
    </Router>
  );
}

export default App;
